import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, ValidatorFn, ValidationErrors, AbstractControl } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControls, FormValuesAsJson } from './configs/interfaces/form.interface';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { BaseAgency, EntitlementsHelperService, LoggedInUserInformationService, UserInfo, UserInfoStateStructure } from '@ptl/account-management';
import { MatSnackBar } from '@angular/material/snack-bar';
import { IndividualMarketConsumerReferralEmailService } from './services/individual-market-consumer-referral-email.service';
import { Subscription } from 'rxjs';
import { brandCPA, brandNENY, brandNEPA, brandSEPA, brandWNY, brandWPA } from '@ptl/brand';
import { EmailValidatorService } from '../../services/email-validator.service';
import { GlobalShadowStyleDirective } from '@ptl/global-components';

@Component({
    selector: 'app-individual-market-consumer-referral-email',
    templateUrl: './individual-market-consumer-referral-email.component.html',
    styleUrls: ['./individual-market-consumer-referral-email.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    standalone: false
})
export class IndividualMarketConsumerReferralEmailComponent extends GlobalShadowStyleDirective implements OnInit, OnDestroy {



  @Input() emailTitle: string;
  /** this will no longer be used - it will be overwritten by entitlements-driven logic */
  isTelesales: boolean;
  @Input() prependZeroes: 'true' | 'false';
  @Input() producerIdentifierAs: 'npn' | 'agentACSId';
  @Input() doNotReplyEmailAddress: string;

  form: UntypedFormGroup;
  displayEmailSentMessage = false;
  showSpinner = false;
  entryMask = "(000) 000-0000";

  toEmailFormValueChanges: Subscription;
  contentStorageSubscription: Subscription;

  nyRegionValues = [
    brandWNY,
    brandNENY
  ];

  paRegionValues = [
    brandSEPA,
    brandNEPA,
    brandCPA,
    brandWPA
  ];

  constructor(
    private fb: UntypedFormBuilder,
    private IndividualMarketConsumerReferralEmailService: IndividualMarketConsumerReferralEmailService,
    private reducerUtilityService: ReducerUtilityService,
    private entitlementService: EntitlementsHelperService,
    private matSnackBar: MatSnackBar,
    private loggedInUserInfo: LoggedInUserInformationService
  ) { 
    super();
  }

  async ngOnInit() {

    const lob = await this.entitlementService.getCurrentLobPromise();
    this.isTelesales = await this.loggedInUserInfo.isTelesales(lob);

    // TODO: can we instantiate the form with the correct values and validators instead? this seems to not be reliable.
    this.form = this.fb.group({
      agentPhoneNumber: this.fb.control(null),
      agencyNumber: this.fb.control(null, [
        Validators.required,
        Validators.maxLength(10),
        Validators.pattern(RegExp(/^[a-zA-Z0-9]*$/))
      ]),
      producerNumber: this.fb.control(null),
      fromEmailAddress: this.fb.control('', [
        Validators.required,
        EmailValidatorService.createEmailValidator(),
        Validators.maxLength(75)
      ]),
      toEmailAddress: this.fb.control('', [
        Validators.required,
        EmailValidatorService.createEmailValidator(),
        Validators.maxLength(75)
      ]), // Customer email (1)
      toEmailAddressRepeat: this.fb.control('', [
        Validators.required,
        EmailValidatorService.createEmailValidator(),
        Validators.maxLength(75),
        this.emailConfirmedValidator()
      ]), // Customer email (2)
      clientName: this.fb.control('', [
        Validators.required,
        Validators.maxLength(100)
      ]), // Customer Name
      region: this.fb.control(null) // Customer region for NY only
    });

    //this.contentStorageSubscription = this.contentStorageService.contentChanged$.subscribe(async () => {
    //  if (this.producerAgencyHelperService.isAgencyInfoAvailable) {


    /** Requires content storage to be available */
    const selectedAgency = await this.entitlementService.getSelectedAgencyPromise(lob);

    this.populateFormFromState(selectedAgency);
    this.setValidators();
    if (!(this.toEmailFormValueChanges)) {
      this.toEmailFormValueChanges = this.formControls.toEmailAddress.valueChanges.subscribe(() => {
        this.manualEmailConfirmedValidator();
      });
    }
    //  }
    //});
  }

  ngOnDestroy() {
    if (this.contentStorageSubscription) {
      this.contentStorageSubscription.unsubscribe();
    }

    if (this.toEmailFormValueChanges) {
      this.toEmailFormValueChanges.unsubscribe();
    }
  }


  onSubmit() {

    this.displayEmailSentMessage = false;
    this.form.markAllAsTouched();

    if (this.form.valid) {
      this.showSpinner = true;
      this.IndividualMarketConsumerReferralEmailService
        .triggerLinkShareEmail(this.formValuesAsJson, this.isTelesales, this.prependZeroes?.toUpperCase() === 'TRUE')
        .then(response => {
          if (!(response instanceof HttpErrorResponse)) {
            // hide form and show complete message
            this.displayEmailSentMessage = true;
            this.form.controls['clientName'].reset();
            this.form.controls['toEmailAddress'].reset();
            this.form.controls['toEmailAddressRepeat'].reset();
            this.matSnackBar.open(`Successfully sent email.`, 'OK', {
              duration: 2000,
              panelClass: 'positive-panel-class'
            });
          }
        }).catch(error => console.error(error))
        .finally(() => {
          this.showSpinner = false
        });
    }
  }


  setValidators() {

    /** Phone Number */
    /** Phone number is hidden via template when telesales */

      this.formControls.agentPhoneNumber.setValidators([
        Validators.required,
        Validators.maxLength(14),
        Validators.pattern(RegExp(/^[0-9() -]{14}$/))
      ]);

    /** Producer Number */
    if (this.producerIdentifierAs.toUpperCase() === 'NPN') {
      this.formControls.producerNumber.setValidators([
        Validators.required,
        Validators.maxLength(10)
      ]);
    } else {
      this.formControls.producerNumber.setValidators([
        Validators.required,
        Validators.maxLength(10),
        Validators.minLength(5)
      ]);
    }

    /** Region for NY and PA */
    if (this.isNewYorkRegion() || this.isPARegion()) {
      this.formControls.region.setValidators([
        Validators.required
      ]);
    }

    // Trigger updates
    this.formControls.agentPhoneNumber.updateValueAndValidity();
    this.formControls.producerNumber.updateValueAndValidity();
    this.formControls.region.updateValueAndValidity();

    // This line does not appear to trigger the above methods and only checks the current status of each field
    // which is why we have to manually retrigger each individual control's validity function
    this.form.updateValueAndValidity();
  }


  manualEmailConfirmedValidator() {
    if (this.formControls
      && this.formControls.toEmailAddressRepeat
      && (this.formControls.toEmailAddressRepeat.touched ||
        this.formControls.toEmailAddressRepeat.dirty)
    ) {
      this.formControls.toEmailAddressRepeat.updateValueAndValidity();
    }
  }

  emailConfirmedValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value && control.value !== this.formControls.toEmailAddress.value) {
        return { emailNoMatch: 'Emails do not match' };
      }
      return null;
    };
  }

  populateFormFromState(selectedAgency: BaseAgency) {
    const appStates = this.reducerUtilityService.getApplicationStates();
    const loginInfo: UserInfoStateStructure = appStates['LoginInfo'];
    const producerData: UserInfo = loginInfo.userInfo;

    /** Email Address ( use Do Not Reply stuff for telesales) */
    if (this.isTelesales) {
      this.formControls.fromEmailAddress.setValue(this.doNotReplyEmailAddress);
      this.formControls.fromEmailAddress.updateValueAndValidity();
      this.formControls.fromEmailAddress.disable({ emitEvent: false });
    } else {
      if (producerData.email){
        this.formControls.fromEmailAddress.setValue(producerData.email);
      }
    }

    /** Phone Number */
    this.formControls.agentPhoneNumber.setValue(producerData.bus_phone || producerData.telephone);

    /** Agency Number */
    this.formControls.agencyNumber.setValue(selectedAgency.agencyId);

    /** Producer Number */
    switch (this.producerIdentifierAs) {
      case 'npn': this.formControls.producerNumber.setValue(selectedAgency.npnNo); break;
      case 'agentACSId': this.formControls.producerNumber.setValue(selectedAgency.agentACSId); break;
      default: this.formControls.producerNumber.setValue(selectedAgency.npnNo); break;
    }

  }

  get formValuesAsJson(): FormValuesAsJson {
    // form.value does not return disabled control values; getRawValue() does
    return this.form.getRawValue() as unknown as FormValuesAsJson;
  }

  get formControls(): FormControls {
    return this.form.controls as unknown as FormControls;
  }

  isNewYorkRegion(): boolean {
    return this.IndividualMarketConsumerReferralEmailService.isNewYorkRegion();
  }

  isPARegion(): boolean {
    return this.IndividualMarketConsumerReferralEmailService.isPARegion();
  }

  removeOf(description: string): string {
    const pattern = /( of )?(Western New York|North Eastern New York)?$/;
    return description.replace(pattern, '');
  }
  

}
